import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Plus, MapPin, Building2, Hash, Trash2, X, Pencil, Save, Link2, Palette, Camera, Layers, Tag } from 'lucide-react';

const ICON_STYLES = [
  { id: 'circle',  label: 'Circle',  preview: (c) => `<div style="width:24px;height:24px;border-radius:50%;background:${c};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:11px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)">P</div>` },
  { id: 'square',  label: 'Square',  preview: (c) => `<div style="width:24px;height:24px;border-radius:4px;background:${c};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:11px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)">P</div>` },
  { id: 'diamond', label: 'Diamond', preview: (c) => `<div style="width:20px;height:20px;background:${c};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:10px;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3);transform:rotate(45deg)"><span style="transform:rotate(-45deg)">P</span></div>` },
  { id: 'pin',     label: 'Pin',     preview: (c) => `<div style="width:24px;height:32px;background:${c};border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3)"><span style="transform:rotate(45deg);color:#fff;font-weight:bold;font-size:10px">P</span></div>` },
  { id: 'star',    label: 'Star',    preview: (c) => `<div style="width:28px;height:28px;background:${c};clip-path:polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%);display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-weight:bold;font-size:9px;margin-top:4px">P</span></div>` },
];
const ICON_LABELS = ['P', 'G', 'S', 'A', 'B', 'C', '1', '2', '3', '\u2605'];
const ICON_COLORS = [
  { name: 'Green', color: '#10b981' }, { name: 'Blue', color: '#3b82f6' }, { name: 'Red', color: '#ef4444' },
  { name: 'Orange', color: '#f97316' }, { name: 'Purple', color: '#8b5cf6' }, { name: 'Teal', color: '#14b8a6' },
  { name: 'Pink', color: '#ec4899' }, { name: 'Yellow', color: '#eab308' }, { name: 'Navy', color: '#1e3a5f' }, { name: 'Black', color: '#374151' },
];
const parseCoordinates = (input) => {
  if (!input || typeof input !== 'string') return null;
  const t = input.trim();
  let m;
  if ((m = t.match(/destination=([-\d.]+),([-\d.]+)/))) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  if ((m = t.match(/@([-\d.]+),([-\d.]+)/))) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  if ((m = t.match(/\/place\/([-\d.]+),([-\d.]+)/))) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  if ((m = t.match(/[?&]q=([-\d.]+),([-\d.]+)/))) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  if ((m = t.match(/ll=([-\d.]+),([-\d.]+)/))) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
  if ((m = t.match(/^([-\d.]+)[,\s]+([-\d.]+)$/))) {
    const lat = parseFloat(m[1]), lng = parseFloat(m[2]);
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) return { lat, lng };
  }
  return null;
};

// Generate detailed availability data with levels and custom tag allocations
const generateDetailedData = (totalSpaces, locationType, numLevels, hasTopDeck, customTags) => {
  const timestamp = new Date().toISOString();
  const overallOccRate = 0.2 + Math.random() * 0.65;
  const overallOccupied = Math.round(totalSpaces * overallOccRate);
  const overallAvailable = totalSpaces - overallOccupied;

  let levels = [];
  if (locationType === 'garage' && numLevels > 0) {
    const levelCount = numLevels + (hasTopDeck ? 1 : 0);
    let remaining = totalSpaces;
    for (let i = 0; i < levelCount; i++) {
      const isLast = i === levelCount - 1;
      const isTopDeck = hasTopDeck && i === levelCount - 1;
      const baseShare = totalSpaces / levelCount;
      const variance = isLast ? remaining : Math.round(baseShare * (0.8 + Math.random() * 0.4));
      const levelTotal = isLast ? remaining : Math.min(variance, remaining);
      remaining -= levelTotal;
      const levelOccRate = Math.max(0.05, Math.min(0.98, overallOccRate + (Math.random() - 0.5) * 0.4));
      const levelOccupied = Math.round(levelTotal * levelOccRate);
      levels.push({
        name: isTopDeck ? 'Top Deck' : `Level ${i + 1}`,
        totalSpaces: levelTotal, occupiedSpaces: levelOccupied,
        availableSpaces: levelTotal - levelOccupied,
        utilizedPercent: levelTotal > 0 ? Math.round((levelOccupied / levelTotal) * 100) : 0,
      });
    }
  }

  // Build attributes from custom tags with explicit space allocations
  let attributes = [];
  let allocatedSpaces = 0;
  const tags = customTags && customTags.length > 0 ? customTags : [];
  for (const tag of tags) {
    const tagTotal = Math.max(0, parseInt(tag.spaces) || 0);
    allocatedSpaces += tagTotal;
    const tagOccRate = Math.max(0, Math.min(1, overallOccRate + (Math.random() - 0.5) * 0.5));
    const tagOccupied = Math.round(tagTotal * tagOccRate);
    attributes.push({
      name: tag.name, totalSpaces: tagTotal, occupiedSpaces: tagOccupied,
      availableSpaces: tagTotal - tagOccupied,
      utilizedPercent: tagTotal > 0 ? Math.round((tagOccupied / tagTotal) * 100) : 0,
    });
  }
  // Remaining spaces become "General"
  const generalSpaces = Math.max(0, totalSpaces - allocatedSpaces);
  if (generalSpaces > 0) {
    const genOccRate = Math.max(0, Math.min(1, overallOccRate + (Math.random() - 0.5) * 0.2));
    const genOccupied = Math.round(generalSpaces * genOccRate);
    attributes.unshift({
      name: 'General', totalSpaces: generalSpaces, occupiedSpaces: genOccupied,
      availableSpaces: generalSpaces - genOccupied,
      utilizedPercent: generalSpaces > 0 ? Math.round((genOccupied / generalSpaces) * 100) : 0,
    });
  }

  return {
    overall: { totalCount: totalSpaces, occupiedCount: overallOccupied, availableCount: overallAvailable, utilizedPercent: (overallOccupied / totalSpaces).toFixed(8), timestamp },
    levels,
    attributes,
  };
};

const AddLocationForm = ({ fakeLocations, onAddLocation, onRemoveLocation, onUpdateLocation, onClearAll, accentColor }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [totalSpaces, setTotalSpaces] = useState('');
  const [locationType, setLocationType] = useState('garage');
  const [numLevels, setNumLevels] = useState(3);
  const [hasTopDeck, setHasTopDeck] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagSpaces, setNewTagSpaces] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [iconColor, setIconColor] = useState('#3b82f6');
  const [iconStyle, setIconStyle] = useState('circle');
  const [iconLabel, setIconLabel] = useState('P');
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [detectedCoords, setDetectedCoords] = useState(null);
  const [geocodeWarning, setGeocodeWarning] = useState('');
  const photoInputRef = useRef(null);

  const geocodeAddress = async (addr) => {
    const strategies = [addr, addr.replace(/^\d+\s+/, ''), addr.replace(/^[\d\s]+/, '').replace(/^,\s*/, '')];
    const parts = addr.split(',').map(p => p.trim()).filter(Boolean);
    if (parts.length >= 2) { strategies.push(parts.slice(1).join(', ')); strategies.push(parts[parts.length - 2] + ', ' + parts[parts.length - 1]); }
    if (parts.length >= 1) strategies.push(parts[parts.length - 1]);
    for (const query of strategies) {
      if (!query || query.length < 3) continue;
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`, { headers: { 'User-Agent': 'ParkingManagementApp/1.0' } });
        const results = await response.json();
        if (results && results.length > 0) return { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) };
        await new Promise(r => setTimeout(r, 300));
      } catch (e) { console.error('Geocoding error:', query, e); }
    }
    return null;
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`, { headers: { 'User-Agent': 'ParkingManagementApp/1.0' } });
      const result = await r.json();
      return result?.display_name || null;
    } catch { return null; }
  };

  const handleAddressChange = (v) => { setAddress(v); setGeocodeWarning(''); setDetectedCoords(parseCoordinates(v)); };

  const addCustomTag = () => {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    if (selectedTags.some(t => t.name.toLowerCase() === trimmed.toLowerCase())) return;
    const spaces = parseInt(newTagSpaces) || 0;
    setSelectedTags(prev => [...prev, { name: trimmed, spaces: Math.max(0, spaces) }]);
    setNewTagName('');
    setNewTagSpaces('');
  };

  const removeCustomTag = (index) => {
    setSelectedTags(prev => prev.filter((_, i) => i !== index));
  };

  const updateTagSpaces = (index, value) => {
    setSelectedTags(prev => prev.map((t, i) => i === index ? { ...t, spaces: Math.max(0, parseInt(value) || 0) } : t));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const img = new Image();
      img.onload = () => {
        const MAX_DIM = 600;
        let w = img.width, h = img.height;
        if (w > MAX_DIM || h > MAX_DIM) {
          const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        setPhotoUrl(canvas.toDataURL('image/jpeg', 0.5));
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const startEdit = (loc) => {
    const usage = loc.latestSiteParkingUsages.nodes[0];
    setEditingId(loc.id); setName(loc.displayName); setAddress(loc.address1);
    setTotalSpaces(String(usage.totalCount));
    setLocationType(loc.locationType || 'garage');
    setNumLevels(loc.numLevels || 3);
    setHasTopDeck(loc.hasTopDeck !== false);
    setSelectedTags(loc.customTags || []);
    setPhotoUrl(loc.photoUrl || '');
    setIconColor(loc.iconColor || '#3b82f6'); setIconStyle(loc.iconStyle || 'circle'); setIconLabel(loc.iconLabel || 'P');
    setDetectedCoords(null); setGeocodeWarning(''); setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false); setEditingId(null); setName(''); setAddress(''); setTotalSpaces('');
    setLocationType('garage'); setNumLevels(3); setHasTopDeck(true); setSelectedTags([]);
    setPhotoUrl(''); setIconColor('#3b82f6'); setIconStyle('circle'); setIconLabel('P');
    setDetectedCoords(null); setGeocodeWarning('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !address.trim() || !totalSpaces) return;
    const total = parseInt(totalSpaces);
    if (total <= 0 || isNaN(total)) return;
    setIsGeocoding(true);

    const existingLoc = editingId ? fakeLocations.find(l => l.id === editingId) : null;
    const addressChanged = !existingLoc || existingLoc.address1 !== address.trim();
    let coords = existingLoc?.coordinates || null;
    let displayAddress = address.trim();

    if (addressChanged) {
      const parsed = parseCoordinates(address);
      coords = parsed || await geocodeAddress(address);
      if (parsed) {
        const readable = await reverseGeocode(parsed.lat, parsed.lng);
        displayAddress = readable || `${parsed.lat.toFixed(6)}, ${parsed.lng.toFixed(6)}`;
      }
    } else {
      displayAddress = existingLoc?.address1 || address.trim();
    }

    if (!coords) {
      setGeocodeWarning('Could not find map coordinates. Try a Google Maps URL or raw coordinates.');
      setIsGeocoding(false); return;
    }

    const detailed = generateDetailedData(total, locationType, numLevels, hasTopDeck, selectedTags);
    const locationData = {
      displayName: name.trim(), address1: displayAddress, coordinates: coords,
      locationType, numLevels: locationType === 'garage' ? numLevels : 0,
      hasTopDeck: locationType === 'garage' ? hasTopDeck : false,
      customTags: selectedTags,
      tags: selectedTags.map(t => t.name),
      photoUrl,
      iconColor, iconStyle, iconLabel,
      levels: detailed.levels, attributes: detailed.attributes,
      latestSiteParkingUsages: { nodes: [detailed.overall] },
    };

    if (editingId) {
      onUpdateLocation({ ...existingLoc, ...locationData });
    } else {
      onAddLocation({ id: `fake-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, directions: '', isFake: true, ...locationData });
    }
    cancelForm(); setIsGeocoding(false);
  };

  const btnColor = accentColor || '#3b82f6';
  const currentStyleDef = ICON_STYLES.find(s => s.id === iconStyle) || ICON_STYLES[0];

  return (
    <div className="space-y-4">
      {fakeLocations.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-slate-700">Custom Locations ({fakeLocations.length})</h4>
            <Button variant="outline" size="sm" onClick={onClearAll} className="text-red-500 border-red-200 hover:bg-red-50 text-xs">
              <Trash2 className="w-3 h-3 mr-1" />Clear All
            </Button>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {fakeLocations.map((loc) => {
              const usage = loc.latestSiteParkingUsages.nodes[0];
              const isEditing = editingId === loc.id;
              const locStyle = ICON_STYLES.find(s => s.id === (loc.iconStyle || 'circle')) || ICON_STYLES[0];
              return (
                <div key={loc.id} className={`flex items-center justify-between rounded-lg p-3 border transition-colors ${isEditing ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                    <div dangerouslySetInnerHTML={{ __html: locStyle.preview(loc.iconColor || '#3b82f6').replace('>P<', `>${loc.iconLabel || 'P'}<`) }} className="flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">{loc.displayName}</div>
                      <div className="text-xs text-slate-500">
                        {loc.locationType === 'surface' ? 'Surface Lot' : `Garage · ${(loc.levels || []).length} levels`}
                      </div>
                      <div className="text-xs text-slate-500">{usage.availableCount} / {usage.totalCount} available</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 ml-2">
                    <button onClick={() => startEdit(loc)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => onRemoveLocation(loc.id)} className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-red-50" title="Remove"><X className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!showForm ? (
        <Button onClick={() => { setEditingId(null); setShowForm(true); }} variant="outline" className="w-full border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-600 hover:text-blue-600 py-6">
          <Plus className="w-5 h-5 mr-2" />Add Parking Location
        </Button>
      ) : (
        <Card className="border-2 border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-800">{editingId ? 'Edit Location' : 'Add New Location'}</CardTitle>
              <button onClick={cancelForm} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-1.5"><Building2 className="w-3.5 h-3.5" /><span>Location Name</span></label>
                <Input type="text" placeholder="e.g., North Campus Garage" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>

              {/* Address */}
              <div>
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-1.5"><MapPin className="w-3.5 h-3.5" /><span>Address / Coordinates / Google Maps URL</span></label>
                <Input type="text" placeholder="Address, coordinates, or Google Maps link" value={address} onChange={(e) => handleAddressChange(e.target.value)} required />
                {detectedCoords ? (
                  <div className="flex items-center space-x-1.5 mt-1.5"><Link2 className="w-3 h-3 text-green-600" /><span className="text-xs text-green-600 font-medium">Coordinates: {detectedCoords.lat.toFixed(6)}, {detectedCoords.lng.toFixed(6)}</span></div>
                ) : <p className="text-xs text-slate-500 mt-1">Accepts: address, lat/lng, or Google Maps URL</p>}
                {geocodeWarning && <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md"><p className="text-xs text-amber-700 font-medium">{geocodeWarning}</p></div>}
              </div>

              {/* Location Type */}
              <div className="border-t border-slate-200 pt-4">
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-2"><Layers className="w-3.5 h-3.5" /><span>Location Type</span></label>
                <div className="flex space-x-2">
                  <button type="button" onClick={() => setLocationType('garage')}
                    className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${locationType === 'garage' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                    Garage
                  </button>
                  <button type="button" onClick={() => setLocationType('surface')}
                    className={`flex-1 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${locationType === 'surface' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                    Surface Lot
                  </button>
                </div>
              </div>

              {/* Garage: Levels */}
              {locationType === 'garage' && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Number of Levels</label>
                  <div className="flex items-center space-x-3">
                    <Input type="number" min="1" max="15" value={numLevels} onChange={(e) => setNumLevels(parseInt(e.target.value) || 1)} className="w-24" />
                    <label className="flex items-center space-x-2 text-sm text-slate-600 cursor-pointer">
                      <input type="checkbox" checked={hasTopDeck} onChange={(e) => setHasTopDeck(e.target.checked)} className="rounded border-slate-300" />
                      <span>+ Top Deck</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Total Spaces */}
              <div>
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-1.5"><Hash className="w-3.5 h-3.5" /><span>Total Parking Spaces</span></label>
                <Input type="number" min="1" placeholder="e.g., 500" value={totalSpaces} onChange={(e) => setTotalSpaces(e.target.value)} required />
              </div>

              {/* Custom Tags with Space Allocation */}
              <div>
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-2"><Tag className="w-3.5 h-3.5" /><span>Custom Tags</span></label>
                <p className="text-xs text-slate-500 mb-2">Add tags with dedicated space counts. Remaining spaces become "General".</p>
                {/* Existing tags list */}
                {selectedTags.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {selectedTags.map((tag, idx) => {
                      const totalNum = parseInt(totalSpaces) || 0;
                      const allocated = selectedTags.reduce((s, t) => s + (parseInt(t.spaces) || 0), 0);
                      return (
                        <div key={idx} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg p-2" data-testid={`custom-tag-row-${idx}`}>
                          <span className="text-sm font-medium text-slate-700 flex-1 truncate">{tag.name}</span>
                          <Input
                            type="number" min="0" max={totalNum}
                            value={tag.spaces}
                            onChange={(e) => updateTagSpaces(idx, e.target.value)}
                            className="w-20 text-sm text-center"
                            data-testid={`custom-tag-spaces-${idx}`}
                          />
                          <span className="text-xs text-slate-400">spaces</span>
                          <button type="button" onClick={() => removeCustomTag(idx)}
                            className="p-1 text-slate-400 hover:text-red-500 rounded hover:bg-red-50" data-testid={`custom-tag-remove-${idx}`}>
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Add new tag row */}
                <div className="flex items-center gap-2">
                  <Input
                    type="text" placeholder="Tag name (e.g., ADA)"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
                    className="flex-1 text-sm"
                    data-testid="new-tag-name-input"
                  />
                  <Input
                    type="number" min="0" placeholder="#"
                    value={newTagSpaces}
                    onChange={(e) => setNewTagSpaces(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
                    className="w-20 text-sm text-center"
                    data-testid="new-tag-spaces-input"
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addCustomTag}
                    disabled={!newTagName.trim()}
                    className="text-xs whitespace-nowrap"
                    data-testid="add-tag-btn">
                    <Plus className="w-3 h-3 mr-1" />Add
                  </Button>
                </div>
                {/* Summary: remaining general spaces */}
                {totalSpaces && parseInt(totalSpaces) > 0 && (
                  <div className="mt-2 text-xs text-slate-500 bg-slate-50 rounded-md p-2" data-testid="tag-allocation-summary">
                    <span className="font-medium">General spaces:</span>{' '}
                    {Math.max(0, parseInt(totalSpaces) - selectedTags.reduce((s, t) => s + (parseInt(t.spaces) || 0), 0))} of {totalSpaces} remaining
                    {selectedTags.reduce((s, t) => s + (parseInt(t.spaces) || 0), 0) > parseInt(totalSpaces) && (
                      <span className="text-red-500 font-medium ml-2">(over-allocated!)</span>
                    )}
                  </div>
                )}
              </div>

              {/* Photo Upload */}
              <div>
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-2"><Camera className="w-3.5 h-3.5" /><span>Photo (optional)</span></label>
                {photoUrl ? (
                  <div className="relative inline-block">
                    <img src={photoUrl} alt="Location" className="h-24 w-auto rounded-lg border border-slate-200 object-cover" />
                    <button type="button" onClick={() => { setPhotoUrl(''); if (photoInputRef.current) photoInputRef.current.value = ''; }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600"><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div>
                    <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" id="loc-photo" />
                    <label htmlFor="loc-photo" className="cursor-pointer flex items-center justify-center w-full h-16 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors">
                      <div className="text-center"><Camera className="w-5 h-5 text-slate-400 mx-auto" /><span className="text-xs text-slate-500">Upload photo</span></div>
                    </label>
                  </div>
                )}
              </div>

              {/* Icon Customization */}
              <div className="border-t border-slate-200 pt-4">
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-3"><Palette className="w-3.5 h-3.5" /><span>Map Icon</span></label>
                <div className="flex items-center space-x-3 mb-3 p-2 bg-white rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-500">Preview:</span>
                  <div dangerouslySetInnerHTML={{ __html: currentStyleDef.preview(iconColor).replace('>P<', `>${iconLabel}<`) }} />
                  <span className="text-xs text-slate-600 font-medium">{currentStyleDef.label}</span>
                </div>
                <div className="mb-3">
                  <span className="text-xs text-slate-500 mb-1.5 block">Color</span>
                  <div className="flex flex-wrap gap-1.5">
                    {ICON_COLORS.map(c => (<button key={c.color} type="button" onClick={() => setIconColor(c.color)} className={`w-7 h-7 rounded-md border-2 transition-all hover:scale-110 ${iconColor === c.color ? 'border-slate-800 ring-2 ring-offset-1 ring-slate-300' : 'border-slate-200'}`} style={{ backgroundColor: c.color }} title={c.name} />))}
                    <input type="color" value={iconColor} onChange={(e) => setIconColor(e.target.value)} className="w-7 h-7 rounded-md cursor-pointer border border-slate-200" />
                  </div>
                </div>
                <div className="mb-3">
                  <span className="text-xs text-slate-500 mb-1.5 block">Shape</span>
                  <div className="flex flex-wrap gap-2">
                    {ICON_STYLES.map(s => (<button key={s.id} type="button" onClick={() => setIconStyle(s.id)} className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all ${iconStyle === s.id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}><div dangerouslySetInnerHTML={{ __html: s.preview(iconColor) }} /><span className="text-[10px] text-slate-500 mt-1">{s.label}</span></button>))}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-slate-500 mb-1.5 block">Label</span>
                  <div className="flex flex-wrap gap-1.5">
                    {ICON_LABELS.map(l => (<button key={l} type="button" onClick={() => setIconLabel(l)} className={`w-7 h-7 rounded-md border-2 text-xs font-bold transition-all ${iconLabel === l ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>{l}</button>))}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <Button type="submit" disabled={!name.trim() || !address.trim() || !totalSpaces || isGeocoding} className="flex-1 text-white" style={{ backgroundColor: btnColor }}>
                  {isGeocoding ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Locating...</>) : editingId ? (<><Save className="w-4 h-4 mr-2" />Save Changes</>) : (<><Plus className="w-4 h-4 mr-2" />Add Location</>)}
                </Button>
                <Button type="button" variant="outline" onClick={cancelForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export { ICON_STYLES };
export default AddLocationForm;
